import { useCallback, useEffect, useMemo, useState } from 'react'
import { Box, CircularProgress, Dialog, DialogContent, IconButton, Stack, Typography, useMediaQuery } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import CloseIcon from '@mui/icons-material/Close'
import { useSearchParams } from 'react-router-dom'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@/contexts/AuthContext'
import {
  billingService,
  getBillingErrorCode,
  type BillingAccount,
  type Plan,
  type PlanPackage,
  type PlansCatalogue,
  type SubscriptionState,
} from '@/services/billingService'
import ResearcherShell from '@/components/researcher/ResearcherShell'
import UsageHero from '@/components/researcher/plans/UsageHero'
import BillingDetails from '@/components/researcher/plans/BillingDetails'
import NextStepCard from '@/components/researcher/plans/NextStepCard'
import BillingPeriodToggle, {
  type BillingPeriod,
} from '@/components/researcher/plans/BillingPeriodToggle'
import PlanCard, { type PlanStanding } from '@/components/researcher/plans/PlanCard'
import PlansNotice from '@/components/researcher/plans/PlansNotice'
import PurchaseRecordedDialog from '@/components/researcher/plans/PurchaseRecordedDialog'
import { formatAmount } from '@/components/researcher/plans/price'
import { LANGUAGE_INTL_LOCALE } from '@/i18n/types'
import { useLocale } from '@/contexts/LocaleContext'

/** How a payment provider tells us how the trip out went. */
type PurchaseReturn = 'ok' | 'cancelled'

/**
 * The billing periods on offer, gathered across every plan that sells one.
 *
 * Derived rather than declared: the page shows whatever the catalogue sells, so
 * adding a six-month package to `plans.py` puts a third segment on the toggle
 * without a frontend release. The discount shown is the one a customer would
 * actually get, taken from the entry plan — the plans use the same ladder.
 */
function billingPeriods(plans: Plan[]): BillingPeriod[] {
  const byMonths = new Map<number, BillingPeriod>()
  for (const plan of plans) {
    for (const pkg of plan.packages) {
      if (!byMonths.has(pkg.months)) {
        byMonths.set(pkg.months, {
          months: pkg.months,
          discountPercent: pkg.discount_percent,
        })
      }
    }
  }
  return [...byMonths.values()].sort((a, b) => a.months - b.months)
}

/** The period the page opens on: the one the catalogue recommends. */
function defaultMonths(plans: Plan[]): number {
  for (const plan of plans) {
    const preferred = plan.packages.find((pkg) => pkg.recommended)
    if (preferred) return preferred.months
  }
  return 1
}

/**
 * Which single card carries the recommended mark.
 *
 * The payload flags a recommended *package* per plan, which is one flag too
 * many for a screen that is allowed exactly one mark — and recommending the
 * plan somebody is already on, or one they have outgrown, would be advice
 * nobody can act on. So the mark goes to the first plan past the current one
 * that has a recommendation to make; when there is none (the account is at the
 * top, or the plan above is the one we quote by hand) no card is marked.
 *
 * A plan that is only announced is skipped too: "recommended" over a button
 * that cannot be pressed is advice nobody can act on either.
 */
function recommendedPlanId(plans: Plan[], currentPlan: string): string | null {
  const currentIndex = plans.findIndex((plan) => plan.id === currentPlan)
  const candidate = plans.find(
    (plan, index) =>
      index > currentIndex &&
      plan.on_sale !== false &&
      plan.packages.some((pkg) => pkg.recommended),
  )
  return candidate?.id ?? null
}

/**
 * The plans screen: where an account reads what it is on, and stops being it.
 *
 * Laid out as the account first (Z-126, variant A of
 * `brain/99-zmiany/2026-10-08-1416-plan-i-pakiety-od-nowa`): most visits are
 * from somebody who already pays and wants the counter or an invoice, so the
 * respondents left, the next charge, the card and the invoices come first.
 * Below them sits one suggestion — the plan above this one and what it adds —
 * and the full side-by-side lives behind "compare all plans".
 *
 * Every number about the offer comes from `GET /api/billing/plans/`, and every
 * number about money already spent from `GET /api/billing/account/` (Stripe).
 * Nothing about either is written down in the frontend.
 */
export default function PlansPage() {
  const { t } = useTranslation('plans')
  const theme = useTheme()
  const c = theme.affexy.colors
  const fullScreenDialog = useMediaQuery(theme.breakpoints.down('md'))
  const { language } = useLocale()
  const { user } = useAuth()
  const [searchParams, setSearchParams] = useSearchParams()

  // undefined = loading, null = the catalogue could not be read
  const [catalogue, setCatalogue] = useState<PlansCatalogue | null | undefined>(undefined)
  const [account, setAccount] = useState<BillingAccount | undefined>(undefined)
  const [accountFailed, setAccountFailed] = useState(false)
  const [months, setMonths] = useState(1)
  const [compareOpen, setCompareOpen] = useState(false)
  const [busyPackageId, setBusyPackageId] = useState<string | null>(null)
  const [failure, setFailure] = useState<{ packageId: string; message: string } | null>(null)
  const [recorded, setRecorded] = useState<{ pkg: PlanPackage; planName: string } | null>(null)
  const [purchaseReturn, setPurchaseReturn] = useState<PurchaseReturn | null>(null)

  useEffect(() => {
    let cancelled = false
    billingService
      .getPlans()
      .then((data) => {
        if (cancelled) return
        setCatalogue(data)
        setMonths(defaultMonths(data.plans))
      })
      .catch(() => !cancelled && setCatalogue(null))
    // Separate and slower — a round trip to Stripe. The counter does not wait
    // for it; the billing rows hold their place until it lands.
    billingService
      .getAccount()
      .then((data) => !cancelled && setAccount(data))
      .catch(() => !cancelled && setAccountFailed(true))
    return () => {
      cancelled = true
    }
  }, [])

  // The payment provider sends people back with ?purchase=ok|cancelled. Read
  // it once and take it out of the URL: a refresh half an hour later must not
  // re-announce a payment, and a shared link must not announce somebody
  // else's.
  useEffect(() => {
    const value = searchParams.get('purchase')
    if (value !== 'ok' && value !== 'cancelled') return
    setPurchaseReturn(value)
    const next = new URLSearchParams(searchParams)
    next.delete('purchase')
    setSearchParams(next, { replace: true })
  }, [searchParams, setSearchParams])

  const accountEmail = user?.email ?? ''
  const salesEmail = catalogue?.sales_email ?? ''

  /** Where the plan that is agreed with a person is agreed. */
  const contactHref = useMemo(() => {
    if (!salesEmail) return ''
    const subject = encodeURIComponent(t('mail.subject'))
    const body = encodeURIComponent(t('mail.body', { email: accountEmail }))
    return `mailto:${salesEmail}?subject=${subject}&body=${body}`
  }, [salesEmail, accountEmail, t])

  const handleBuy = useCallback(
    async (pkg: PlanPackage, planName: string) => {
      setBusyPackageId(pkg.id)
      setFailure(null)
      try {
        const result = await billingService.startCheckout(pkg.id)
        if (result.status === 'checkout') {
          // Leaving the app entirely — no state set after this matters.
          window.location.assign(result.url)
          return
        }
        setRecorded({ pkg, planName })
      } catch (error) {
        const code = getBillingErrorCode(error)
        setFailure({
          packageId: pkg.id,
          message:
            code === 'unknown_package'
              ? t('purchase.errorUnknownPackage')
              : code === 'plan_not_on_sale'
                ? t('purchase.errorNotOnSale')
                : t('purchase.errorGeneric', { email: salesEmail }),
        })
      } finally {
        setBusyPackageId(null)
      }
    },
    [salesEmail, t],
  )

  // Cancelling does not reload the price list — nothing in it changes — so the
  // one field that did is patched in place.
  const handleSubscriptionChange = useCallback((next: SubscriptionState | null) => {
    setCatalogue((previous) =>
      previous ? { ...previous, current: { ...previous.current, subscription: next } } : previous,
    )
  }, [])

  if (catalogue === undefined) {
    return (
      <ResearcherShell active="plans" crumbs={[t('title')]} hideTopBarAction>
        <Box sx={{ display: 'grid', placeItems: 'center', minHeight: 320 }}>
          <CircularProgress />
        </Box>
      </ResearcherShell>
    )
  }

  if (catalogue === null) {
    return (
      <ResearcherShell active="plans" crumbs={[t('title')]} hideTopBarAction>
        <Box sx={{ maxWidth: 560, mx: 'auto', py: 8 }}>
          <PlansNotice
            icon={<ErrorOutlineIcon sx={{ fontSize: 18 }} />}
            body={t('loadFailed')}
            tone="accent"
          />
        </Box>
      </ResearcherShell>
    )
  }

  const { plans, current } = catalogue
  const periods = billingPeriods(plans)
  // A trial is not ownership. The account sits on the FaceWise feature set and
  // has bought nothing, so nothing is "the plan you are already on" — treating
  // it as one is what left a trial with a greyed-out "your current plan" where
  // its only way to pay should be.
  const ownedPlan = current.on_trial ? '' : current.plan
  const currentIndex = plans.findIndex((plan) => plan.id === ownedPlan)
  const marked = recommendedPlanId(plans, ownedPlan)
  const currentPlanName = t(`plan.${current.plan}.name`, { defaultValue: current.plan })
  const ownedPackage =
    plans[currentIndex]?.packages.find((pkg) => pkg.recommended) ?? plans[currentIndex]?.packages[0]
  // The one suggestion: the plan straight above this one, whether it is sold
  // from a page, announced, or agreed with a person.
  const nextPlan = plans[currentIndex + 1] ?? null
  const enterprise = plans.find((plan) => !plan.self_serve) ?? null
  const showEnterpriseNote = enterprise !== null && nextPlan?.id !== enterprise.id && ownedPlan !== enterprise.id
  const limit = current.respondents.limit
  const nextTitle = current.on_trial
    ? t('next.titleTrial')
    : limit
      ? t('next.titleLimit', { limit: limit.toLocaleString(LANGUAGE_INTL_LOCALE[language]) })
      : t('next.title')

  const failureFor = (plan: Plan) =>
    failure && plan.packages.some((pkg) => pkg.id === failure.packageId) ? failure.message : undefined

  return (
    <ResearcherShell active="plans" crumbs={[t('title')]} hideTopBarAction>
      <Box sx={{ maxWidth: 960, mx: 'auto', width: '100%', pb: 6 }}>
        <Typography
          sx={{ fontSize: 12.5, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase', color: c.ink56 }}
        >
          {ownedPackage
            ? `${currentPlanName} · ${t('price.netPerMonth', { amount: formatAmount(ownedPackage.price_per_month_pln) })}`
            : currentPlanName}
        </Typography>
        <Typography
          component="h1"
          sx={{
            fontFamily: theme.affexy.fonts.heading,
            fontSize: { xs: 28, md: 34 },
            fontWeight: 700,
            letterSpacing: '-0.02em',
            lineHeight: 1.1,
            mt: 0.75,
            mb: 3,
          }}
        >
          {t('standing.label')}
        </Typography>

        <Stack spacing={2}>
          {purchaseReturn === 'ok' && (
            <PlansNotice
              icon={<CheckCircleOutlineIcon sx={{ fontSize: 18 }} />}
              title={t('purchase.okTitle')}
              body={t('purchase.okBody')}
              tone="accent"
            />
          )}
          {purchaseReturn === 'cancelled' && (
            <PlansNotice
              icon={<InfoOutlinedIcon sx={{ fontSize: 18 }} />}
              title={t('purchase.cancelledTitle')}
              body={t('purchase.cancelledBody')}
            />
          )}

          <UsageHero current={current} />

          {current.subscription?.active && (
            <BillingDetails
              account={account}
              failed={accountFailed}
              subscription={current.subscription}
              onSubscriptionChange={handleSubscriptionChange}
            />
          )}
        </Stack>

        {nextPlan && (
          <>
            <Typography
              component="h2"
              sx={{
                fontFamily: theme.affexy.fonts.heading,
                fontSize: 22,
                fontWeight: 700,
                letterSpacing: '-0.01em',
                mt: 5,
                mb: 1.5,
              }}
            >
              {nextTitle}
            </Typography>
            <NextStepCard
              plan={nextPlan}
              ownedPlanName={ownedPlan ? currentPlanName : null}
              onBuy={(pkg) => void handleBuy(pkg, t(`plan.${nextPlan.id}.name`, { defaultValue: nextPlan.id }))}
              onCompare={() => setCompareOpen(true)}
              busy={nextPlan.packages.some((pkg) => pkg.id === busyPackageId)}
              error={failureFor(nextPlan)}
              contactHref={contactHref}
            />
          </>
        )}

        {showEnterpriseNote && enterprise && (
          <Box
            sx={{
              mt: 2,
              bgcolor: c.white,
              borderRadius: '20px',
              p: { xs: 2.5, md: 3 },
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { sm: 'center' },
              justifyContent: 'space-between',
              gap: 1.5,
              boxShadow: '0 1px 2px rgba(27,28,56,.04), 0 8px 24px rgba(27,28,56,.05)',
            }}
          >
            <Box>
              <Typography sx={{ fontFamily: theme.affexy.fonts.heading, fontSize: 17, fontWeight: 700 }}>
                {t(`plan.${enterprise.id}.name`)}
              </Typography>
              <Typography sx={{ fontSize: 14, color: c.ink56 }}>{t(`plan.${enterprise.id}.tagline`)}</Typography>
            </Box>
            <Box
              component="a"
              href={contactHref || undefined}
              sx={{ fontWeight: 700, color: c.coral, textDecoration: 'none', whiteSpace: 'nowrap' }}
            >
              {t('actions.contact')} ›
            </Box>
          </Box>
        )}

        {!nextPlan && (
          <Box sx={{ mt: 3 }}>
            <Box
              component="button"
              type="button"
              onClick={() => setCompareOpen(true)}
              sx={{ all: 'unset', cursor: 'pointer', fontWeight: 700, color: c.coral }}
            >
              {t('next.compare')} ›
            </Box>
          </Box>
        )}
      </Box>

      <Dialog
        open={compareOpen}
        onClose={() => setCompareOpen(false)}
        fullScreen={fullScreenDialog}
        maxWidth="lg"
        fullWidth
        scroll="body"
      >
        <DialogContent sx={{ p: { xs: 2, md: 4 }, bgcolor: c.gray50 }}>
          <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between', mb: 2.5 }}>
            <Typography
              component="h2"
              sx={{ fontFamily: theme.affexy.fonts.heading, fontSize: 24, fontWeight: 700, letterSpacing: '-0.015em' }}
            >
              {t('next.compareTitle')}
            </Typography>
            <IconButton onClick={() => setCompareOpen(false)} aria-label={t('purchase.close')}>
              <CloseIcon />
            </IconButton>
          </Stack>
          <Stack spacing={2.5}>
            <BillingPeriodToggle
              periods={periods}
              selectedMonths={months}
              onSelect={setMonths}
              disabled={busyPackageId !== null}
            />
            <Box
              sx={{
                display: 'grid',
                gap: 2.5,
                gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
                alignItems: 'start',
              }}
            >
              {plans.map((plan, index) => {
                const standing: PlanStanding =
                  currentIndex < 0
                    ? 'available'
                    : index === currentIndex
                      ? 'current'
                      : index < currentIndex
                        ? 'included'
                        : 'available'
                const planName = t(`plan.${plan.id}.name`, { defaultValue: plan.id })
                return (
                  <PlanCard
                    key={plan.id}
                    plan={plan}
                    standing={standing}
                    recommended={plan.id === marked}
                    selectedMonths={months}
                    onBuy={(pkg) => void handleBuy(pkg, planName)}
                    busy={plan.packages.some((pkg) => pkg.id === busyPackageId)}
                    contactHref={contactHref}
                    error={failureFor(plan)}
                  />
                )
              })}
            </Box>
          </Stack>
        </DialogContent>
      </Dialog>

      <PurchaseRecordedDialog
        pkg={recorded?.pkg ?? null}
        planName={recorded?.planName ?? ''}
        accountEmail={accountEmail}
        onClose={() => setRecorded(null)}
      />
    </ResearcherShell>
  )
}
